import { inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { filter, groupBy, map, mergeMap, share, switchMap, tap, throttleTime, withLatestFrom } from 'rxjs/operators'

import { MessagingService } from '@app/modules/messaging/services/messaging.service'
import type { NotificationType } from '@app/modules/notifications'
import { NotificationsChannelService } from '@app/modules/notifications'
import { NotificationService } from '@app/overlays/notification/notification.service'
import { NotificationsStore } from '@app/overlays/notification/notifications.store'
import * as fromFeature from '@store/notifications'

const isThreadRelated = (type: NotificationType) =>
  ['thread_created', 'thread_updated', 'message_photo_sent', 'voice_message_sent'].includes(type)

@Injectable()
export class NotificationsEffects {
  private readonly actions$ = inject(Actions)
  private readonly notificationService = inject(NotificationService)
  private readonly notificationsStore = inject(NotificationsStore)
  private readonly notificationsChannelService = inject(NotificationsChannelService)
  private readonly messagingService = inject(MessagingService)

  private readonly subscription$ = this.actions$.pipe(
    ofType(fromFeature.Actions.initialize),
    switchMap(() => this.notificationsChannelService.notifications()),
  )

  readonly messages$ = createEffect(() =>
    this.subscription$.pipe(
      map(notification => fromFeature.Actions.received({ notification })),
      share(),
    ),
  )

  private readonly showableNotifications$ = this.messages$.pipe(
    withLatestFrom(this.notificationsStore.isShowingCredits$),
    filter(([{ notification }, isShowingCredits]) => {
      const conversationWithProfileIsOpen = this.messagingService.isConversationOpen(
        notification.sref_params.profile_browse_id,
      )

      if (isThreadRelated(notification.note_type) && conversationWithProfileIsOpen) {
        return false
      }

      return !isShowingCredits
    }),
    map(([{ notification }]) => notification),
  )

  readonly showNotification$ = createEffect(
    () =>
      this.showableNotifications$.pipe(
        groupBy(({ note_type, source_profile_id }) => `${note_type}-${source_profile_id}`),
        mergeMap(group => group.pipe(throttleTime(10_000))),
        tap(notification => this.notificationService.show(notification)),
      ),
    { dispatch: false },
  )
}
